import type { ChangeEvent, MouseEvent } from 'react'
import { useCallback, useId, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import useToggle from 'hooks/useToggle'
import noop from 'lib/function/noop'
import type { TripPlannerNavigationData } from './ChatTripPlannerPage/hooks/useTripPlannerNavigation'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import ChatListSectionLabel from './ChatListSectionLabel'
import ChatListShowMoreToggle from './ChatListShowMoreToggle'
import TripPlannerDrawerFooter from './TripPlannerDrawerFooter'
import TripPlannerChatSearchResults from './TripPlannerChatSearchResults'
import TripPlannerNavigationChatRow from './TripPlannerNavigationChatRow'
import TripPlannerNavigationTripRow from './TripPlannerNavigationTripRow'
import TripPlannerNewChatRow from './TripPlannerNewChatRow'
import TripPlannerSearchChatsRow from './TripPlannerSearchChatsRow'
import TripPlannerStaffTools from './TripPlannerStaffTools'
import { isModifiedClick } from './isModifiedClick'
import {
  MIN_CHAT_SEARCH_QUERY_LENGTH,
  TRIP_PLANNER_NAVIGATION_GUTTER,
  TRIP_PLANNER_NAVIGATION_ROW_INSET,
} from './tripPlannerNavigationConstants'

const INITIAL_VISIBLE_CHATS = 6

const NavigationLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  min-block-size: 0;
`

const NavigationRows = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: ${rem(TRIP_PLANNER_NAVIGATION_ROW_INSET)};
`

const GutteredBlock = styled.div`
  padding-inline: ${rem(TRIP_PLANNER_NAVIGATION_GUTTER)};
`

const ChatsSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(8)};
  min-block-size: 0;
`

const ChatHistoryList = styled.div`
  min-block-size: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Rows arrive after the loading indicator; anchoring would scroll the first chats out of view. */
  overflow-anchor: none;
`

const SearchResults = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-block-size: 0;
`

const BottomControls = styled.div`
  flex-shrink: 0;
  padding-inline: ${rem(TRIP_PLANNER_NAVIGATION_GUTTER)};
`

interface Props extends TripPlannerNavigationData {
  isLoggedIn: boolean
  isLandingView: boolean
  onStartNewChat: () => void
  onNavigate?: () => void
  onChatSelect: (chatId: number) => void
  activeChatId: number | undefined
  isStaff: boolean
  isDevMode: boolean
  onDevModeToggle: () => void
  isRewindMode: boolean
  onRewindModeToggle: () => void
}

function TripPlannerNavigation({
  isLoggedIn,
  isLandingView,
  onStartNewChat,
  onNavigate = noop,
  recentItems,
  isRecentItemsLoading,
  isRecentItemsError,
  onRecentItemsRetry,
  onChatSelect,
  onChatRename,
  onChatDelete,
  activeChatId,
  onActiveChatDownloadPdf,
  onActiveChatDownloadSpreadsheet,
  isStaff,
  isDevMode,
  onDevModeToggle,
  isRewindMode,
  onRewindModeToggle,
}: Props) {
  const intl = useIntl()
  const chatListId = useId()
  const [searchQuery, setSearchQuery] = useState('')
  const [showAllChats, toggleShowAllChats] = useToggle()
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const trimmedQuery = searchQuery.trim()
  const isSearching = trimmedQuery.length >= MIN_CHAT_SEARCH_QUERY_LENGTH
  const isSearchActive = isSearchFocused || trimmedQuery.length > 0

  const [visibleRecentItems] = useListTruncator(showAllChats ? undefined : INITIAL_VISIBLE_CHATS, recentItems)
  const showRecentChats = isLoggedIn && !isSearching
  const hasRecentItems = recentItems.length > 0
  const hiddenChatCount = recentItems.length - INITIAL_VISIBLE_CHATS
  const showChatsToggle = showRecentChats && hiddenChatCount > 0
  const showRecentItemsError = showRecentChats && isRecentItemsError && !isRecentItemsLoading && !hasRecentItems
  const showEmptyChats = showRecentChats && !isRecentItemsError && !isRecentItemsLoading && !hasRecentItems

  const handleSearchFocus = useCallback(() => setIsSearchFocused(true), [])
  const handleSearchBlur = useCallback(() => setIsSearchFocused(false), [])

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value)
  }, [])

  const handleSearchClear = useCallback(() => setSearchQuery(''), [])

  const handleNewChat = useCallback(() => {
    setSearchQuery('')
    onNavigate()
    onStartNewChat()
  }, [onNavigate, onStartNewChat])

  const handleChatSelect = useCallback((chatId: number) => {
    setSearchQuery('')
    onNavigate()
    onChatSelect(chatId)
  }, [onChatSelect, onNavigate])

  const handleChatDelete = useCallback((chatId: number) => {
    onNavigate()
    onChatDelete(chatId)
  }, [onChatDelete, onNavigate])

  // Classic trips are a full navigation to their own page, so only the drawer needs closing.
  const handleClassicTripClick = useCallback((event: MouseEvent) => {
    if (!isModifiedClick(event)) {
      onNavigate()
    }
  }, [onNavigate])

  const handleDevModeToggle = useCallback(() => {
    onNavigate()
    onDevModeToggle()
  }, [onDevModeToggle, onNavigate])

  const handleRewindModeToggle = useCallback(() => {
    onNavigate()
    onRewindModeToggle()
  }, [onNavigate, onRewindModeToggle])

  const newChatLabel = intl.formatMessage({
    id: 'tSel3m929WprS4nNY3O5',
    defaultMessage: 'New chat',
    description: 'Navigation item in the TripPlanner side nav and menu drawer that starts a new AI chat',
  })
  const searchChatsLabel = intl.formatMessage({
    id: '-kkoT9IZ7DmC_Wh91rdC',
    defaultMessage: 'Search chats',
    description: 'Placeholder and accessible label for the chat search input in the TripPlanner side nav and menu drawer',
  })
  const emptyChatsText = intl.formatMessage({
    id: '8PGW7a8-oPZ3rV5hVVIg',
    defaultMessage: 'No chats yet. Your conversations will show up here.',
    description: 'Empty state shown in the TripPlanner side nav and menu drawer when the traveller has no chats yet',
  })
  const recentChatsErrorText = intl.formatMessage({
    id: '7aO_3-VY_5H4RIdl_SVv',
    defaultMessage: 'We couldn’t load your chats.',
    description: 'Message shown in place of the recent chats list in the TripPlanner side nav and menu drawer when the chats failed to load',
  })
  const retryRecentChatsLabel = intl.formatMessage({
    id: 'dpcidf_pcRLQ_Trx819I',
    defaultMessage: 'Try again',
    description: 'Link below the failed recent chats message in the TripPlanner side nav and menu drawer that reloads the chats',
  })
  const showMoreChatsLabel = intl.formatMessage({
    id: 'AbtphK9DUDdZfiHKB4wz',
    defaultMessage: '{count, plural, one {Show # more} other {Show # more}}',
    description: 'Link that reveals the remaining recent chats in the TripPlanner side nav and menu drawer, with the number of chats not yet shown',
  }, { count: hiddenChatCount })
  const staffTools = useMemo(() => isStaff && (
    <TripPlannerStaffTools
      isDevMode={isDevMode}
      isRewindMode={isRewindMode}
      onDevModeToggle={handleDevModeToggle}
      onRewindModeToggle={handleRewindModeToggle}
    />
  ), [handleDevModeToggle, handleRewindModeToggle, isDevMode, isRewindMode, isStaff])

  return (
    <NavigationLayout>
      <NavigationRows>
        <TripPlannerNewChatRow
          label={newChatLabel}
          selected={isLandingView && !isSearchActive}
          onClick={handleNewChat}
        />
        {isLoggedIn && (
          <TripPlannerSearchChatsRow
            label={searchChatsLabel}
            value={searchQuery}
            selected={isSearchActive}
            onChange={handleQueryChange}
            onClear={handleSearchClear}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
          />
        )}
      </NavigationRows>

      <ChatsSection>
        {isSearching && (
          <SearchResults>
            <TripPlannerChatSearchResults query={trimmedQuery} onChatSelect={handleChatSelect} />
          </SearchResults>
        )}
        {showRecentChats && (
          <GutteredBlock>
            <ChatListSectionLabel>
              <FormattedMessage {...tripPlannerGlossaryMessages.recentChats} />
            </ChatListSectionLabel>
          </GutteredBlock>
        )}
        {showRecentChats && hasRecentItems && (
          <ChatHistoryList id={chatListId}>
            {visibleRecentItems.map(item => {
              if (item.type === 'aiChat') {
                const isActiveChat = item.id === activeChatId
                return (
                  <TripPlannerNavigationChatRow
                    key={`${item.type}-${item.id}`}
                    item={item}
                    current={isActiveChat}
                    onSelect={handleChatSelect}
                    onRename={onChatRename}
                    onDelete={handleChatDelete}
                    onDownloadPdf={isActiveChat ? onActiveChatDownloadPdf : undefined}
                    onDownloadSpreadsheet={isActiveChat ? onActiveChatDownloadSpreadsheet : undefined}
                  />
                )
              }

              return (
                <TripPlannerNavigationTripRow
                  key={`${item.type}-${item.id}`}
                  item={item}
                  onClick={handleClassicTripClick}
                />
              )
            })}
          </ChatHistoryList>
        )}
        {showChatsToggle && (
          <GutteredBlock>
            <ChatListShowMoreToggle
              expanded={showAllChats}
              controlsId={chatListId}
              collapsedLabel={showMoreChatsLabel}
              onClick={toggleShowAllChats}
            />
          </GutteredBlock>
        )}
        {showRecentChats && isRecentItemsLoading && !hasRecentItems && (
          <LoadingIndicator inline backdrop="none" height={96} />
        )}
        {showEmptyChats && (
          <GutteredBlock>
            <BodyText variant="small" colour="light-foreground-alt">
              {emptyChatsText}
            </BodyText>
          </GutteredBlock>
        )}
        {showRecentItemsError && (
          <GutteredBlock>
            <Group direction="vertical" gap={4}>
              <BodyText variant="small" colour="light-foreground-alt">
                {recentChatsErrorText}
              </BodyText>
              <TextLink size="small" onClick={onRecentItemsRetry}>
                {retryRecentChatsLabel}
              </TextLink>
            </Group>
          </GutteredBlock>
        )}
      </ChatsSection>

      <BottomControls>
        <TripPlannerDrawerFooter onLinkClick={onNavigate} staffTools={staffTools} />
      </BottomControls>
    </NavigationLayout>
  )
}

export default TripPlannerNavigation
